import { useId } from 'react'

interface ToggleProps {
  checked: boolean
  onChange: (checked: boolean) => void
  label?: string
  disabled?: boolean
  id?: string
}

export function Toggle({
  checked,
  onChange,
  label,
  disabled = false,
  id: providedId,
}: ToggleProps) {
  const generatedId = useId()
  const id = providedId || generatedId

  const containerStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
  }

  const toggleContainerStyles: React.CSSProperties = {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
  }

  const trackStyles: React.CSSProperties = {
    width: '44px',
    height: '24px',
    backgroundColor: checked ? '#10B981' : '#d1d5db',
    borderRadius: '12px',
    transition: 'background-color 0.2s ease',
    position: 'relative',
  }

  const thumbStyles: React.CSSProperties = {
    position: 'absolute',
    top: '2px',
    left: checked ? '22px' : '2px',
    width: '20px',
    height: '20px',
    backgroundColor: 'white',
    borderRadius: '50%',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
    transition: 'left 0.2s ease',
  }

  const labelStyles: React.CSSProperties = {
    fontSize: '14px',
    fontWeight: 500,
    color: '#374151',
    cursor: disabled ? 'not-allowed' : 'pointer',
  }

  // Hidden checkbox for accessibility
  const hiddenInputStyles: React.CSSProperties = {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault()
      if (!disabled) {
        onChange(!checked)
      }
    }
  }

  return (
    <div style={containerStyles}>
      <div
        style={toggleContainerStyles}
        onClick={() => !disabled && onChange(!checked)}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : 0}
        role="switch"
        aria-checked={checked}
        aria-disabled={disabled}
        aria-labelledby={label ? `${id}-label` : undefined}
      >
        <input
          type="checkbox"
          id={id}
          checked={checked}
          onChange={e => onChange(e.target.checked)}
          disabled={disabled}
          style={hiddenInputStyles}
          tabIndex={-1}
        />
        <div style={trackStyles}>
          <div style={thumbStyles} />
        </div>
      </div>
      {label && (
        <label
          id={`${id}-label`}
          htmlFor={id}
          style={labelStyles}
          onClick={() => !disabled && onChange(!checked)}
        >
          {label}
        </label>
      )}
    </div>
  )
}
